<template>
    <div ref="target" :class="{ 'autocomplete-container': relative, 'grow': !relative }" @keydown="changeFocus" @focusout="onFocusOut">
        <div class="button-wrapper">
            <input
                :id="id"
                ref="searchInput"
                v-model="modelValue"
                type="text"
                :aria-describedby="`${id}-initialInstructions`"
                aria-autocomplete="both"
                :placeholder="placeholder"
                autocomplete="off"
                class="autocomplete-input"
                :class="$slots.button ? 'has-x-button' : ''"
                :disabled="disabled"
                @keydown.enter="quickSelect"
            />
            <slot name="button"></slot>
        </div>
        <ul
            v-if="((results.length || addresses.length || stops.length) && showResult && !hideResults) || loading"
            id="results"
            ref="resultList"
            role="listbox"
            tabindex="-1"
            class="no-bullet results-list"
            aria-expanded="false"
        >
            <li v-if="results.length > 0">
                <p v-if="showResultsCategoryNames" class="results-category-name">{{ t('routes') }}</p>
                <ul class="no-bullet">
                    <li v-for="(result, i) in results" :key="i" role="option" tabindex="-1">
                        <slot
                            name="route"
                            :result="result"
                            :index="i"
                            :length="results.length"
                            aria-selected="false"
                        ></slot>
                    </li>
                </ul>
            </li>
            <li v-if="addresses.length > 0">
                <p v-if="showResultsCategoryNames" class="results-category-name">{{ t('addresses') }}</p>
                <ul class="no-bullet">
                    <li
                        v-for="(result, i) in addresses"
                        :key="i"
                        role="option"
                        tabindex="-1"
                        class="addresses-results-item"
                    >
                        <slot
                            name="address"
                            :close="closeResults"
                            :result="result"
                            :index="i"
                            aria-selected="false"
                        ></slot>
                    </li>
                </ul>
            </li>
            <li v-if="stops.length > 0 || loading">
                <p v-if="showResultsCategoryNames" class="results-category-name">{{ t('stops') }}</p>
                <ul v-if="stops.length > 0" class="no-bullet">
                    <li v-for="(result, i) in stops" :key="i" class="stops-results-item" role="option" tabindex="-1">
                        <slot name="stop" :result="result" :index="i" aria-selected="false"></slot>
                    </li>
                </ul>
                <div v-else class="loading">{{ t('loading') }}</div>
            </li>
        </ul>
        <div v-else-if="hint && modelValue.length < 6 && showResult" class="no-results">
            {{ hint }}
        </div>
        <div v-else-if="modelValue !== '' && showResult && showNoResultsMsg" class="no-results">
            {{ t('no_results') }}
        </div>
        <span :id="`${id}-initialInstructions`" class="visuallyhidden" aria-hidden="true">
            {{ t('instructions') }}
        </span>
        <LiveRegion ref="liveRegion" />
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { onClickOutside, useDebounce } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { tabbable } from 'tabbable';
import { useRouter } from 'vue-router';
import Fuse from 'fuse.js';

import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import directionSlug from '@@/js/utils/directionSlug';
import slugify from '@@/js/utils/slugify';
import LiveRegion from '@/shared/LiveRegion';

const modelValue = defineModel();

const props = defineProps({
    id: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '',
    },
    initialShowResult: {
        type: Boolean,
        default: false,
    },
    hideResults: {
        type: Boolean,
        default: false,
    },
    options: {
        type: Object,
        default: () => ({}),
    },
    list: {
        type: Array,
        default: () => [],
    },
    addresses: {
        type: Array,
        default: () => [],
    },
    stops: {
        type: Array,
        default: () => [],
    },
    showResultsCategoryNames: {
        type: Boolean,
        default: true,
    },
    showNoResultsMsg: {
        type: Boolean,
        default: true,
    },
    relative: {
        type: Boolean,
        default: true,
    },
    date: {
        type: String,
        default: undefined,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    hint: {
        type: String,
        default: undefined,
    },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n({
    messages: {
        fr: {
            routes: 'Parcours',
            addresses: 'Adresses',
            stops: 'Arrêts',
            no_results: "Il n'y a aucun résultat pour cette recherche",
            no_results_short: 'Aucun résultat',
            available_results: 'résultat est disponible. | résultats sont disponibles.',
            available_instructions:
                'Utilisez les flèches clavier du haut et du bas pour naviguer et la touche Entrer pour sélectionner une option.',
            instructions:
                "Lorsque les suggestions d'autocomplétion sont disponibles, utilisez les flèches clavier du haut et du bas pour naviguer et la touche Entrer pour sélectionner une option.",
            loading: 'Chargement...',
        },
        en: {
            routes: 'Routes',
            addresses: 'Addresses',
            stops: 'Stops',
            no_results: 'There is no results for this search',
            no_results_short: 'No results',
            available_results: 'result is available. | results are available.',
            available_instructions: 'Use the up and down arrow keys to navigate and Enter key to select an option.',
            instructions:
                'When autocomplete suggestions are available, use the up and down arrow keys to navigate and Enter key to select an option.',
            loading: 'Loading...',
        },
    },
});

const fuse = ref(null);
const throttledSetResultsLiveRegion = ref();

onMounted(() => {
    fuse.value = new Fuse(props.list, props.options);
    throttledSetResultsLiveRegion.value = useDebounce(setResultsLiveRegion, 500);
});

const target = ref();
const showResult = ref(props.initialShowResult);
function closeResults() {
    showResult.value = false;
}
onClickOutside(target, (event) => {
    if (event.type === 'touchstart') return false;
    closeResults();
});

function onFocusOut(event) {
    if (!target.value?.contains(event.relatedTarget)) {
        closeResults();
    }
}

const results = ref([]);
const resultList = ref();
const searchInput = ref();

function changeFocus(event) {
    if ((results.value.length || props.addresses.length || props.stops.length) && showResult.value) {
        const key = event.keyCode;
        if ([38, 40, 27].includes(key)) {
            const tabbableResultList = tabbable(resultList.value);
            const length = tabbableResultList.length;
            if ([38, 40].includes(key)) {
                const index = tabbableResultList.indexOf(event.target);
                const direction = key === 40 ? 1 : -1;
                const newIndex = (index + length + direction) % length;
                tabbableResultList[newIndex].focus();
                tabbableResultList.forEach((result) => {
                    result.setAttribute('aria-selected', false);
                    result.removeAttribute('id');
                });
                tabbableResultList[newIndex].setAttribute('aria-selected', true);
                tabbableResultList[newIndex].setAttribute('id', 'selectedOption');
                searchInput.value.setAttribute('aria-activedescendant', 'selectedOption');
                event.preventDefault();
            } else if (key === 27) {
                results.value = [];
                searchInput.value.focus();
                event.preventDefault();
            }
        }
    }
}

const router = useRouter();
const { getEffectiveDate } = useEffectiveDate();

function quickSelect() {
    if (typeof modelValue !== 'string') {
        return;
    }
    // Match un parcours
    if (modelValue.match(/^[0-9]{1,3}[a-z]?$/gi) && results.value.item.length) {
        const result = results.value.item[0];
        router.push({
            name: 'route-single',
            params: {
                route: result.noParcours,
                direction: directionSlug(result.codeDirectionPrincipale, result.descriptionDirectionPrincipale),
                date: props.date || getEffectiveDate(new Date(), 'yyyy-MM-dd'),
            },
        });
    } else if (modelValue.match(/^[0-9]{4}$/g) && props.stops.length) {
        // Match un arrêt. Un arrêt n'est pas rattaché à un seul parcours/direction, on navigue donc
        // vers la page de l'arrêt (qui liste tous les parcours qui le desservent) plutôt que route-stop.
        const result = props.stops[0];
        router.push({
            name: 'stop-single',
            params: {
                stop: slugify(`${result.entity_id}-${result.entity_title}`),
                date: props.date || getEffectiveDate(new Date(), 'yyyy-MM-dd'),
            },
        });
    }
}

const liveRegion = ref();

function setResultsLiveRegion() {
    const resultsCount = results.value.length + props.addresses.length + props.stops.length;
    if (!props.hideResults) {
        liveRegion.value.setMessage(
            resultsCount !== 0 ? `${resultsCount} ${t('available_results', resultsCount)}` : t('no_results_short'),
        );
    }
}

watch(modelValue, (newValue) => {
    if (newValue.trim() === '') {
        results.value = [];
        searchInput.value.setAttribute('aria-expanded', 'false');
    } else {
        showResult.value = true;
        results.value = fuse.value.search(newValue.trim());
        searchInput.value.setAttribute('aria-expanded', 'true');
    }
});

watch(results, () => throttledSetResultsLiveRegion);
watch(props.addresses, () => throttledSetResultsLiveRegion);
watch(props.stops, () => throttledSetResultsLiveRegion);

function focus() {
    if (searchInput.value && searchInput.value.focus) {
        searchInput.value.focus();
    }
}

defineExpose({ focus });
</script>

<style lang="scss" scoped>
.autocomplete-container {
    position: relative;
}
.grow {
    flex-grow: 1;
}
.button-wrapper {
    position: relative;
}
.results-list {
    z-index: 10;
    position: absolute;
    overflow-y: auto;
    width: 100%;
    max-height: 365px;
    margin: 0;
    background-color: $color-white;
    box-shadow: $floating-shadow;
    bottom: -8px;
    transform: translateY(100%);
    ul {
        margin: 0;
    }
}
.no-results {
    z-index: 13;
    position: absolute;
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin: 0;
    padding: 16px;
    color: $color-black-50;
    border: 1px solid $color-black-10;
    background-color: $color-white;
    box-shadow: $floating-shadow;
    bottom: -8px;
    transform: translateY(100%);
}
.autocomplete-input {
    z-index: 12;
    position: relative;
    margin: 0;
    width: 100%;
    &.has-x-button {
        padding-right: 30px;
        &:focus {
            padding-right: 44px;
        }
        @include breakpoint(medium) {
            padding-right: 44px;
        }
    }
}
.results-category-name {
    @include rem-typo(16px);
    margin: 0;
    padding: 16px;
    font-weight: 700;
    color: $color-black-60;
    background-color: $color-black-8;
}
.stops-results-item,
.addresses-results-item {
    border-bottom: 1px solid $color-black-10;
}
.loading {
    padding: 8px 16px;
}
</style>
